import React from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {OrderFormProps } from './types';
import { ArcAlert, ArcButton, ArcCollapsibleSection, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcStack, ArcText } from '@src/core/ui';
import { useNavigate } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { EMPTY_VALUE, REQUIRED_VALIDATION_TEXT } from '@src/core/config';
import { useDispatch, useSelector } from 'react-redux';
import { getLocationFromCoordinates, setMapCenter } from '@src/core/controllers';
import { useGetHubsQuery } from '@src/features/hubs/redux';

const hasDeliveryAddress = (address?: string) => Boolean(address?.trim() && address !== EMPTY_VALUE);
import { ApiErrorResponse, LocationEntity } from '@src/core/entities';
import { GoogleSearchInput } from '@src/core/components';
import { routesConfig } from '@src/core/routes';
import { setTakeOffLocation, setDestinationLocation, selectTakeOffLocation, selectDestinationLocation } from '@src/features/order/pages/upsert-order-page/redux';
import { useCheckAndPlanMutation, useGetUserDataQuery } from '@src/core/features';
import { getGeocode } from 'use-places-autocomplete';

const COORDINATE_INPUT_PATTERN = /^-?\d*\.?\d*$/;
const GEOCODE_DEBOUNCE_MS = 500;
const HUB_FALLBACK_VALUE = '__user_hub__';

const isCoordinateInput = (value: string) => COORDINATE_INPUT_PATTERN.test(value);

const validateCoordinate = (
  value: string | number,
  min: number,
  max: number,
) => {
  if (value === '' || value == null) return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= min && n <= max;
};

const hasValidCoordinatePair = (
  latitude: string | number,
  longitude: string | number,
) => (
  validateCoordinate(latitude, -90, 90) &&
  validateCoordinate(longitude, -180, 180)
);

const OrderForm: React.FC<OrderFormProps> = (props) => {
  const { form, isSubmitting, isCanceling, onSubmit, goBack, submitLabel = 'Next', showExit = true, embedded = false, hideActions = false, hideDetails = false } = props;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const takeOffLocation = useSelector(selectTakeOffLocation);
  const destinationLocation = useSelector(selectDestinationLocation);

  const [checkAndPlan, { error }] = useCheckAndPlanMutation();
  const { data: hubs = [] } = useGetHubsQuery();
  const { data: userData } = useGetUserDataQuery();
  const geocodeDestTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const destinationLatitude = form.watch('destinationLocation.latitude');
  const destinationLongitude = form.watch('destinationLocation.longitude');

  const onChangeTakeOff = (location: LocationEntity) => {
    const { latitude, longitude } = location;
    form.setValue('takeOffLocation', location);
    dispatch(setTakeOffLocation(location));
    dispatch(setMapCenter({ lat: Number(latitude), lng: Number(longitude) }));
  };

  // Reflect the current takeoff in the (read-only) hub dropdown by matching coordinates;
  // when the takeoff doesn't line up with a hub exactly, fall back to the active/selected
  // hub (or the operator's default hub) so the disabled select still shows a value.
  const defaultHub =
    hubs.find((hub) => hub.selected) ??
    hubs.find((hub) => hub.id === userData?.activeHub?.id) ??
    hubs.find((hub) => hub.id === userData?.hub?.id);
  // The user's hub name, regardless of whether the hubs list has loaded yet — the disabled
  // field is purely informational, so always prefer a name we can resolve from the profile.
  const userHubName =
    defaultHub?.name ??
    userData?.activeHub?.name ??
    userData?.hub?.name ??
    userData?.hubs?.[0]?.name ??
    '';
  const selectedHubId =
    hubs.find(
      (hub) =>
        String(hub.latitude) === String(takeOffLocation?.latitude) &&
        String(hub.longitude) === String(takeOffLocation?.longitude),
    )?.id ?? defaultHub?.id ?? '';

  const onSelectHub = (hubId: number) => {
    const hub = hubs.find((h) => h.id === hubId);
    if (!hub) return;
    onChangeTakeOff({ address: hub.name, latitude: hub.latitude, longitude: hub.longitude });
  };

  const onTakeOffCoordChange = (
    key: 'latitude' | 'longitude',
    value: string,
    onFieldChange: (value: string) => void,
  ) => {
    if (!isCoordinateInput(value)) return;
    onFieldChange(value);

    const current = form.getValues('takeOffLocation');
    const nextLocation: LocationEntity = {
      address: current?.address ?? takeOffLocation?.address ?? '',
      latitude: current?.latitude ?? takeOffLocation?.latitude ?? '',
      longitude: current?.longitude ?? takeOffLocation?.longitude ?? '',
      [key]: value,
    };

    form.setValue('takeOffLocation', nextLocation);
    dispatch(setTakeOffLocation(nextLocation));

    if (hasValidCoordinatePair(nextLocation.latitude, nextLocation.longitude)) {
      dispatch(setMapCenter({
        lat: Number(nextLocation.latitude),
        lng: Number(nextLocation.longitude),
      }));
    }
  };

  const onChangeDestination = (location: LocationEntity) => {
    const { latitude, longitude } = location;
    form.setValue('destinationLocation', location);
    dispatch(setDestinationLocation(location));
    dispatch(setMapCenter({ lat: Number(latitude), lng: Number(longitude) }));
  };

  const syncDestinationCoordsFromForm = React.useCallback(async () => {
    const dest = form.getValues('destinationLocation');
    const latStr = String(dest.latitude ?? '').trim();
    const lngStr = String(dest.longitude ?? '').trim();
    if (!latStr || !lngStr) {
      return;
    }

    const lat = Number(latStr);
    const lng = Number(lngStr);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    const location = await getLocationFromCoordinates(lat, lng);
    if (location) {
      form.setValue('destinationLocation', location);
      dispatch(setDestinationLocation(location));
      dispatch(setMapCenter({ lat: Number(location.latitude), lng: Number(location.longitude) }));
      return;
    }

    if (hasDeliveryAddress(dest.address)) {
      const fallback = {
        address: dest.address,
        latitude: String(lat),
        longitude: String(lng),
      };
      form.setValue('destinationLocation', fallback);
      dispatch(setDestinationLocation(fallback));
      dispatch(setMapCenter({ lat, lng }));
    }
  }, [form, dispatch]);

  const scheduleDestinationGeocode = React.useCallback(() => {
    if (geocodeDestTimerRef.current) {
      clearTimeout(geocodeDestTimerRef.current);
    }
    geocodeDestTimerRef.current = setTimeout(() => {
      void syncDestinationCoordsFromForm();
    }, GEOCODE_DEBOUNCE_MS);
  }, [syncDestinationCoordsFromForm]);

  const flushDestinationGeocode = React.useCallback(() => {
    if (geocodeDestTimerRef.current) {
      clearTimeout(geocodeDestTimerRef.current);
      geocodeDestTimerRef.current = null;
    }
    void syncDestinationCoordsFromForm();
  }, [syncDestinationCoordsFromForm]);

  React.useEffect(() => () => {
    if (geocodeDestTimerRef.current) {
      clearTimeout(geocodeDestTimerRef.current);
    }
  }, []);

  const onDestinationCoordFieldChange = (
    value: string,
    onFieldChange: (value: string) => void,
  ) => {
    onFieldChange(value);
    scheduleDestinationGeocode();
  };

  const onDestinationCoordinateChange = (
    key: 'latitude' | 'longitude',
    value: string,
    onChange: (value: string) => void,
  ) => {
    if (!isCoordinateInput(value)) return;

    onChange(value);

    const current = form.getValues('destinationLocation');
    const nextLocation: LocationEntity = {
      address: current?.address ?? destinationLocation?.address ?? '',
      latitude: current?.latitude ?? destinationLocation?.latitude ?? '',
      longitude: current?.longitude ?? destinationLocation?.longitude ?? '',
      [key]: value,
    };

    dispatch(setDestinationLocation(nextLocation));

    if (hasValidCoordinatePair(nextLocation.latitude, nextLocation.longitude)) {
      dispatch(setMapCenter({
        lat: Number(nextLocation.latitude),
        lng: Number(nextLocation.longitude),
      }));
    }
  };

  React.useEffect(() => {
    const lat = Number(destinationLatitude);
    const lng = Number(destinationLongitude);
    if (!validateCoordinate(destinationLatitude, -90, 90)) return;
    if (!validateCoordinate(destinationLongitude, -180, 180)) return;

    const timer = window.setTimeout(() => {
      getGeocode({ location: { lat, lng } })
        .then((addresses) => {
          const address = addresses[0]?.formatted_address;
          if (!address) return;

          const current = form.getValues('destinationLocation');
          const nextLocation: LocationEntity = {
            ...current,
            address,
            latitude: destinationLatitude,
            longitude: destinationLongitude,
          };

          form.setValue('destinationLocation.address', address, {
            shouldDirty: true,
            shouldValidate: true,
          });
          dispatch(setDestinationLocation(nextLocation));
        })
        .catch(() => {
          // Keep coordinates even if Google cannot resolve an address for them.
        });
    }, GEOCODE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [destinationLatitude, destinationLongitude, dispatch, form]);

  React.useEffect(() => {
    if (!takeOffLocation || !destinationLocation) return;
    if (!hasValidCoordinatePair(takeOffLocation.latitude, takeOffLocation.longitude)) return;
    if (!hasValidCoordinatePair(destinationLocation.latitude, destinationLocation.longitude)) return;
  
    const path1 = { 
      latitude: Number(takeOffLocation.latitude), 
      longitude: Number(takeOffLocation.longitude),
    };
  
    const path2 = {
      latitude: Number(destinationLocation.latitude), 
      longitude: Number(destinationLocation.longitude),
    };
  
    checkAndPlan({
      isSilent: true,
      path: [path1, path2],  
    });
      
  }, [takeOffLocation, destinationLocation]);

  const errors = form.formState.errors;
  const warningError = error as ApiErrorResponse;

  return (
    <ArcStack
      component='form'
      onSubmit={form.handleSubmit(onSubmit)}
      height={embedded ? 'auto' : '100%'}>
      <ArcStack
        height={embedded ? 'auto' : '100%'}
        justifyContent={embedded ? 'flex-start' : 'space-between'}
        spacing={embedded ? 2 : 0}>
        <ArcStack spacing={2.5}>
          <ArcCollapsibleSection title='TAKEOFF'>
            <ArcStack spacing={2}>
              <ArcOutlinedSelect
                disabled
                label='Hub'
                labelProps={{ shrink: true }}
                value={selectedHubId || HUB_FALLBACK_VALUE}
                onChange={(e) => onSelectHub(Number(e.target.value))}
                sx={{
                  '& .MuiSelect-select.Mui-disabled': {
                    WebkitTextFillColor: 'rgba(255, 255, 255, 0.7)',
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}>
                {!selectedHubId ? (
                  <ArcMenuItem value={HUB_FALLBACK_VALUE}>{userHubName}</ArcMenuItem>
                ) : null}
                {hubs.map((hub) => (
                  <ArcMenuItem key={hub.id} value={hub.id}>
                    {hub.name}
                  </ArcMenuItem>
                ))}
              </ArcOutlinedSelect>

              <Controller 
                control={form.control} 
                name='takeOffLocation.address'
                rules={{ required: REQUIRED_VALIDATION_TEXT }}
                render={({ field: { value } }) => (
                  <GoogleSearchInput
                    initValue={value} 
                    label='Takeoff Location'
                    error={Boolean(errors.takeOffLocation?.address)} 
                    helperText={errors.takeOffLocation?.address?.message}
                    onSelect={onChangeTakeOff} />
                )}
              />

              <ArcStack direction='row' spacing={2}>
                <Controller 
                  name='takeOffLocation.latitude'
                  control={form.control} 
                  rules={{
                    required: REQUIRED_VALIDATION_TEXT,
                    validate: (value) => validateCoordinate(value, -90, 90) || 'Invalid latitude',
                  }} 
                  render={({ field }) => (
                    <ArcOutlinedInput 
                      {...field} 
                      label='Latitude' 
                      error={Boolean(errors?.takeOffLocation?.latitude)}
                      helperText={errors?.takeOffLocation?.latitude?.message}
                      onChange={(e) => onTakeOffCoordChange('latitude', e.target.value, field.onChange)}
                    />
                  )} 
                />

                <Controller 
                  name='takeOffLocation.longitude'
                  control={form.control} 
                  rules={{
                    required: REQUIRED_VALIDATION_TEXT,
                    validate: (value) => validateCoordinate(value, -180, 180) || 'Invalid longitude',
                  }} 
                  render={({ field }) => (
                    <ArcOutlinedInput 
                      {...field} 
                      label='Longitude'
                      error={Boolean(errors?.takeOffLocation?.longitude)}
                      helperText={errors?.takeOffLocation?.longitude?.message}
                      onChange={(e) => onTakeOffCoordChange('longitude', e.target.value, field.onChange)}
                    />
                  )} 
                />
              </ArcStack>
            </ArcStack>
          </ArcCollapsibleSection>

          <ArcCollapsibleSection title='DESTINATION'>
            <ArcStack spacing={2}>
              <Controller 
                control={form.control} 
                name='destinationLocation.address' 
                rules={{ required: REQUIRED_VALIDATION_TEXT }}
                render={({ field: { value } }) => (
                  <GoogleSearchInput
                    key={value || 'delivery-location-empty'}
                    initValue={value}
                    label='Delivery Location'
                    error={Boolean(errors.destinationLocation?.address)} 
                    helperText={errors.destinationLocation?.address?.message}
                    onSelect={onChangeDestination} />
                )}
              />
              <ArcStack direction='row' spacing={2}>
                <Controller 
                  name='destinationLocation.latitude'
                  control={form.control} 
                  rules={{
                    required: REQUIRED_VALIDATION_TEXT,
                    validate: (value) => validateCoordinate(value, -90, 90) || 'Invalid latitude',
                  }} 
                  render={({ field }) => (
                    <ArcOutlinedInput 
                      {...field} 
                      label='Latitude' 
                      error={Boolean(errors?.destinationLocation?.latitude)}
                      helperText={errors?.destinationLocation?.latitude?.message}
                      onChange={(e) => onDestinationCoordFieldChange(e.target.value, field.onChange)}
                      onBlur={async (e) => {
                        field.onBlur();
                        await syncDestinationCoordsFromForm();
                      }}
                    />
                  )} 
                />

                <Controller 
                  name='destinationLocation.longitude'
                  control={form.control} 
                  rules={{
                    required: REQUIRED_VALIDATION_TEXT,
                    validate: (value) => validateCoordinate(value, -180, 180) || 'Invalid longitude',
                  }} 
                  render={({ field }) => (
                    <ArcOutlinedInput 
                      {...field} 
                      label='Longitude'
                      error={Boolean(errors?.destinationLocation?.longitude)}
                      helperText={errors?.destinationLocation?.longitude?.message}
                      onChange={(e) => onDestinationCoordFieldChange(e.target.value, field.onChange)}
                      onBlur={() => {
                        field.onBlur();
                        flushDestinationGeocode();
                      }}
                    />
                  )} 
                />

              </ArcStack>
            </ArcStack>
          </ArcCollapsibleSection>

          {Boolean(warningError) && (
            <ArcAlert severity='error' icon={<WarningAmberIcon />} sx={{ bgcolor: 'error.background', py: 0.2, px: 1, '& .MuiAlert-icon': { marginLeft: '3px' } }}>
              <ArcText.h6 color='error.main'> {warningError.data.message} </ArcText.h6>
            </ArcAlert>
          )}

          {!hideDetails ? (
            <ArcCollapsibleSection title='DETAILS'>
              <ArcStack spacing={2}>
                <Controller 
                  name='packageContent'
                  control={form.control} 
                  render={({ field }) => (
                    <ArcOutlinedInput 
                      {...field} 
                      label='Package Content'  />
                  )} 
                />

                <Controller 
                  name='clientName'
                  control={form.control} 
                  render={({ field }) => (
                    <ArcOutlinedInput 
                      {...field} 
                      label='Client Name' />
                  )} 
                />
              </ArcStack>
            </ArcCollapsibleSection>
          ) : null}
        </ArcStack>

        {!hideActions ? (
          <ArcStack spacing={1} width='100%'>
            {!embedded ? (
              <ArcStack direction='row' spacing={1}>
                <ArcButton.ghost
                  fullWidth
                  type='button'
                  onClick={goBack}
                  isLoading={isCanceling}>
                  Cancel
                </ArcButton.ghost>
                {showExit ? (
                  <ArcButton.ghost
                    fullWidth
                    onClick={() => navigate(routesConfig.orders)}
                    isLoading={isSubmitting}>
                    Exit
                  </ArcButton.ghost>
                ) : null}
              </ArcStack>
            ) : null}
            <ArcStack
              direction='row'
              width='100%'
              justifyContent={embedded ? 'flex-end' : 'stretch'}>
              <ArcButton.brand
                fullWidth={!embedded}
                type='submit'
                isLoading={isSubmitting}
                disabled={Boolean(warningError)}
                sx={embedded ? { width: 176, minWidth: 176 } : undefined}>
                {submitLabel}
              </ArcButton.brand>
            </ArcStack>
          </ArcStack>
        ) : null}
      </ArcStack>
    </ArcStack>
  );
};

export default OrderForm;